import React, { FC, useState, useEffect } from 'react';
import { ui } from 'util/ui';

/**
 * Which buckets to show on a project page (UI refresh, Nov 2026).
 * Sits below the project details, with "Show all" first. Clicking a bucket while all are shown
 * shows just that one; after that, each click toggles a bucket on or off.
 */

const BUCKETS: { key: string; label: string }[] = [
  { key: 'Videos', label: 'Videos & Audio' },
  { key: 'Assets', label: 'Assets' },
  { key: 'Notes', label: 'Notes' },
];
const ALL = BUCKETS.map((b) => b.key);

type Props = {
  onSelect(selected: string[]): void;
};

const pill = (on: boolean): React.CSSProperties => ({
  height: 36,
  padding: '0 16px',
  borderRadius: 999,
  border: `1px solid ${on ? ui.ink : ui.control}`,
  background: on ? ui.ink : ui.surface,
  color: on ? '#FFFFFF' : ui.text2,
  font: 'inherit',
  fontSize: 13,
  fontWeight: on ? 600 : 400,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const Selector: FC<Props> = ({ onSelect }) => {
  const [selected, setSelected] = useState<string[]>(ALL);
  const allShown = ALL.every((k) => selected.includes(k));

  useEffect(() => {
    onSelect(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const toggle = (key: string) => {
    if (allShown) return setSelected([key]);
    setSelected((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
  };

  return (
    <div role="group" aria-label="Show sections" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontFamily: ui.font }}>
      <span style={{ fontSize: 13, color: ui.muted, marginRight: 4 }}>Show</span>
      <button type="button" className="archy-btn" aria-pressed={allShown} style={pill(allShown)} onClick={() => setSelected(ALL)}>
        Show all
      </button>
      {BUCKETS.map((b) => {
        const on = !allShown && selected.includes(b.key);
        return (
          <button key={b.key} type="button" className="archy-btn" aria-pressed={on} style={pill(on)} onClick={() => toggle(b.key)}>
            {b.label}
          </button>
        );
      })}
    </div>
  );
};

export default Selector;
